import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { UIDate, useAccountSettings, useSocialCoachSession } from "@/utils";
import {
  VideoPageModal,
  VideoPageStatsBadges,
  VideoPageSourceModal,
  VideoPagesSearch,
  VideoPageOwnerModal,
} from "@/containers/VideoPages";
import { GetContentPagesFilters, Media } from "@/services/bedrock";
import LibraryCard from "@/components/Cells/LibraryCard";
import { get } from "lodash";
import { Svg } from "@/assets";
import { Button, Spinner, Pagination } from "@/components/ui";
import { useContentPageSearch } from "@/utils/useContentPageSearch";
import { EmptyView } from "@/components/EmptyView";
import { WebComponents } from "@/interfaces";
import { SuperPageIcon, SuperPagesListPreview, SuperPageAssignmentsListPreview } from "@/containers/SuperPages";
import { toast } from "sonner";

export const VideoPagesList: React.FC = () => {
  const { isCoach } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();

  const [page, setPage] = useState(1);
  const [videoPageModalInitialObject, setVideoPageModalInitialObject] = useState<
    { videoPageId: number } | { media: Media }
  >();
  const [playerIdOverride, setPlayerIdOverride] = useState<number | string | undefined>(undefined);
  const [videoPageOwnerModalOpen, setVideoPageOwnerModalOpen] = useState(false);
  const [videoPageSourceModalOpen, setVideoPageSourceModalOpen] = useState(false);
  const [searchFilters, setSearchFilters] = useState<GetContentPagesFilters>({});

  const { searchContentPages } = useContentPageSearch();
  const history = useHistory();
  const location = useLocation();

  const clearNewQueryParam = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("new")) {
      searchParams.delete("new");
      const newSearch = searchParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      });
    }
  };

  const withWritePermission = useMemo(() => {
    if (!isCoach) return false;
    return hasWritePermission(WebComponents.VIDEO_PAGES);
  }, [isCoach, hasWritePermission]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("new") === "true") {
      if (isCoach && withWritePermission) {
        setVideoPageOwnerModalOpen(true);
      }
    }
  }, [location.search, isCoach, withWritePermission]);

  const {
    contentPages,
    isFetching: loading,
    isError,
  } = searchContentPages({
    ...searchFilters,
    page,
    perPage: 21,
    sort: "created_date",
    order: "desc",
    extraFields: ["viewSummary"],
  });

  const videoPages = contentPages?.content ?? [];
  const totalPages = contentPages?.pagination?.totalPages ?? 0;

  const handleSearchChange = (searchFilters: GetContentPagesFilters) => {
    setSearchFilters(searchFilters);
    setPage(1);
  };

  useEffect(() => {
    if (isError) toast.error("Failed to fetch video pages");
  }, [isError]);

  return (
    <div className="flex flex-col gap-6">
      {isCoach ? <SuperPagesListPreview /> : <SuperPageAssignmentsListPreview />}

      <div className="flex flex-col gap-4">
        <div className="flex items-center flex-wrap justify-between gap-4">
          <div className="text-xl font-semibold text-foreground">{isCoach ? "Individual " : ""}Video Pages</div>
          {withWritePermission && (
            <>
              <Button
                children="New Video Page"
                onClick={() => {
                  if (isCoach) {
                    setVideoPageOwnerModalOpen(true);
                  } else {
                    setVideoPageSourceModalOpen(true);
                  }
                }}
              />
              <VideoPageOwnerModal
                open={videoPageOwnerModalOpen}
                onOpenChange={open => {
                  setVideoPageOwnerModalOpen(open);
                  if (!open) {
                    clearNewQueryParam();
                  }
                }}
                onComplete={playerId => {
                  setPlayerIdOverride(playerId);
                  setVideoPageOwnerModalOpen(false);
                  setVideoPageSourceModalOpen(true);
                  clearNewQueryParam();
                }}
              />
              <VideoPageSourceModal
                open={videoPageSourceModalOpen}
                onOpenChange={setVideoPageSourceModalOpen}
                onMediaSelected={media => {
                  setVideoPageSourceModalOpen(false);
                  setVideoPageModalInitialObject({ media });
                }}
              />
            </>
          )}
        </div>
        <VideoPagesSearch onSearchChange={handleSearchChange} />

        <div className="flex flex-col">
          <div className="flex flex-wrap gap-x-5">
            {videoPages.map(page => {
              return (
                <LibraryCard
                  key={`video-page-${page.id}`}
                  title={page.displayName || <i>No title</i>}
                  thumbnail={get(page, "thumbnailUrl", Svg.PromptPlaceholder) ?? undefined}
                  dateLabel={UIDate(page.createdDate || new Date().toLocaleDateString())}
                  floatingIcon={!!page.superPageId && <SuperPageIcon />}
                  className={"h-65 min-h-65"}
                  disabled={loading}
                  extra={<VideoPageStatsBadges videoPage={page} />}
                  onClick={() => {
                    setVideoPageModalInitialObject({ videoPageId: page.id });
                  }}
                />
              );
            })}
          </div>
          {videoPages.length === 0 &&
            (loading ? (
              <Spinner className="mx-auto my-10 size-10" />
            ) : (
              <EmptyView text="No Video Pages found." customIconImage={Svg.Library} />
            ))}
        </div>

        {videoPages.length > 0 && (
          <div>
            <Pagination
              page={page}
              totalPages={totalPages}
              boundaryRange={3}
              onPageChange={setPage}
              disabled={loading}
            />
          </div>
        )}

        <VideoPageModal
          open={!!videoPageModalInitialObject}
          initialObject={videoPageModalInitialObject}
          playerIdOverride={typeof playerIdOverride === "number" ? playerIdOverride : undefined}
          onClose={() => setVideoPageModalInitialObject(undefined)}
        />
      </div>
    </div>
  );
};
